// src/Components/Sidebar/Sidebar.js
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { FaAddressCard } from 'react-icons/fa';
import PropTypes from 'prop-types';
import {
  FaTachometerAlt,
  FaFan,
  FaTint,
  FaFilter,
  FaFireExtinguisher,
  FaAws,
  FaTools,
  FaClipboardList,
  FaChevronLeft,
  FaChevronDown,
  FaChevronRight,
  FaFaucet,
  FaSignOutAlt,
  FaBars,
} from 'react-icons/fa';
import { Offcanvas, OverlayTrigger, Tooltip } from 'react-bootstrap';
import './Sidebar.css';
import InternalSidebarAccount from './InternalSidebarAccount';
import SidebarAccount from './SidebarAccount';

function headerKeyForItem(items, itemKey) {
  let current = null;
  for (const item of items) {
    if (item && item.kind === 'header') current = item.key;
    else if (item && item.key === itemKey) return current;
  }
  return null;
}

const navItems = [
  { key: 'hvac', label: 'In-Suite Maintenance', icon: <FaTachometerAlt />, enabled: true },
  { key: 'mech', label: 'Mechanical Room', icon: <FaTools />, enabled: true },
  { key: 'dryerVents', label: 'Dryer Vents', icon: <FaFan />, enabled: true },
  { key: 'stackCleaning', label: 'Stack Cleaning', icon: <FaFaucet />, enabled: true },
  { key: 'waterSavings', label: 'Water Savings', icon: <FaTint />, enabled: true },
  // { key: 'reports', label: 'Reports', icon: <FaClipboardList />, enabled: true }, // Commented out as requested
];

function SidebarContent({ activeKey, onNavigate, collapsed, onToggle, tabs, buildingName, buildingPhotoUrl, managerName, showProjectsTab = false, showValveAuditTab = false, showLogout = false, hubLayout = false, defaultCollapsedSections = [], internalUser, mobile = false }) {
  const navigate = useNavigate();
  const expandedWidth = 240;
  const collapsedWidth = 72;
  const width = collapsed ? collapsedWidth : expandedWidth;
  const brandSrc = collapsed ? '/sidebar-min.png' : '/sidebar.png';

  // Add state to track if sidebar is fully expanded
  const [isFullyExpanded, setIsFullyExpanded] = React.useState(false);
  // Hub-only: designated categories start collapsed on each page load. Other
  // dashboards never opt into this, so they keep a flat nav.
  const [collapsedSections, setCollapsedSections] = React.useState(
    () => new Set(hubLayout ? defaultCollapsedSections : [])
  );

  const computedNavItems = React.useMemo(() => {
    const baseItems = [...navItems];
    if (showProjectsTab) {
      baseItems.splice(1, 0, { key: 'projects', label: 'Projects', icon: <FaClipboardList />, enabled: true });
    }
    if (showValveAuditTab) {
      const insertIdx = showProjectsTab ? 2 : 1;
      baseItems.splice(insertIdx, 0, { key: 'valveAudit', label: 'Valve Audit', icon: <FaFilter />, enabled: true });
    }
    return baseItems;
  }, [showProjectsTab, showValveAuditTab]);

  // Use custom tabs if provided, otherwise default navItems
  const itemsToRender = tabs && Array.isArray(tabs) && tabs.length > 0 ? tabs.map(tab => ({
    ...tab,
    icon: tab.icon || <FaClipboardList />,
    enabled: true,
  })) : computedNavItems;

  // Check if this is a corporate dashboard (has tabs)
  const isCorporateDashboard = tabs && Array.isArray(tabs) && tabs.length > 0;
  const showManagerAccount = !hubLayout && !isCorporateDashboard;
  const hasAccountFooter = hubLayout || showManagerAccount;
  const managerInitials = (managerName || buildingName || 'Building').trim().split(/\s+/)
    .map((word) => word[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

  // Handle sidebar expansion state
  React.useEffect(() => {
    if (collapsed) {
      setIsFullyExpanded(false);
    } else {
      // Delay setting fully expanded to match sidebar transition time
      const timer = setTimeout(() => {
        setIsFullyExpanded(true);
      }, 150); // Slightly before sidebar is fully expanded
      return () => clearTimeout(timer);
    }
  }, [collapsed]);

  // Keep the section that contains the active tab open so a deep-link
  // or in-app navigation never lands on a hidden current page.
  React.useEffect(() => {
    if (!hubLayout) return;
    const headerKey = headerKeyForItem(itemsToRender, activeKey);
    if (!headerKey || !collapsedSections.has(headerKey)) return;
    setCollapsedSections((prev) => {
      if (!prev.has(headerKey)) return prev;
      const next = new Set(prev);
      next.delete(headerKey);
      return next;
    });
  }, [hubLayout, activeKey, itemsToRender, collapsedSections]);

  const toggleSection = (headerKey) => {
    if (!headerKey) return;
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(headerKey)) next.delete(headerKey);
      else next.add(headerKey);
      return next;
    });
  };

  const renderNavItem = (item, idx) => {
    // Collapsible section header. Parents group subsequent items under a
    // small uppercase label (see MaintenanceHub's HUB_TABS). Clicking the
    // header toggles its children. When the sidebar itself is collapsed
    // we hide the label and keep a thin rule so the icon groups stay
    // visually separated.
    if (item.kind === 'header') {
      // Internal categories have a divider above them, including the first
      // category when it follows the standalone Daily Overview tab.
      const isFirstHeader = idx === 0 || !itemsToRender
        .slice(0, idx)
        .some((it) => it && it.kind === 'header');

      const sectionCollapsed = collapsedSections.has(item.key);
      if (collapsed) {
        return (
          <li
            key={item.key || `hdr-${idx}`}
            className="sidebar__section-header"
            aria-hidden
            onClick={() => toggleSection(item.key)}
            style={{
              listStyle: 'none',
              height: 1,
              background: '#E6E8EC',
              margin: '10px 14px 6px',
              cursor: 'pointer',
            }}
          />
        );
      }
      return (
        <li
          key={item.key || `hdr-${idx}`}
          className={`sidebar__section-header${hubLayout && idx > 0 ? ' sidebar__section-header--divided' : ''}`}
          role="button"
          tabIndex={0}
          aria-expanded={!sectionCollapsed}
          onClick={() => toggleSection(item.key)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleSection(item.key);
            }
          }}
          style={{
            listStyle: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            padding: hubLayout ? '5px 0.95rem' : '0 0.95rem',
            margin: hubLayout ? '6px 0 2px' : '6px 0 4px',
            fontSize: hubLayout ? 10 : 10.5,
            lineHeight: hubLayout ? '14px' : undefined,
            flexShrink: hubLayout ? 0 : undefined,
            fontWeight: 700,
            color: '#9CA3AF',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            userSelect: 'none',
            whiteSpace: 'nowrap',
            borderTop: hubLayout || isFirstHeader ? 'none' : '1px solid #E6E8EC',
            paddingTop: hubLayout ? 5 : isFirstHeader ? 0 : 14,
            marginTop: isFirstHeader ? 6 : hubLayout ? 8 : 14,
            cursor: 'pointer',
          }}
        >
          <span>{item.label}</span>
          <span style={{ fontSize: 10, display: 'inline-flex', color: '#C0C4CC' }}>
            {sectionCollapsed ? <FaChevronRight /> : <FaChevronDown />}
          </span>
        </li>
      );
    }

    const isActive = activeKey === item.key;
    const isSub = !!item.indent;
    // Tighter vertical spacing for sub-items (items grouped under a
    // header) so they read as a single category cluster rather than
    // independent peers. Header rendered above already takes care of
    // visual hierarchy.
    const marginBottom = hubLayout ? 0 : isSub ? 2 : 8;
    // Slightly indent sub-items when the sidebar is expanded so the
    // category header reads as a parent of the items below it. When
    // collapsed, every item centres regardless — no room to indent.
    const leftPad = collapsed ? 0 : (isSub ? '1.4rem' : '0.75rem');
    const navItem = (
      <li
        key={item.key}
        role="button"
        tabIndex={item.enabled ? 0 : -1}
        aria-label={item.label}
        aria-current={isActive ? 'page' : undefined}
        onClick={() => item.enabled && onNavigate?.(item.key)}
        onKeyDown={(event) => {
          if (item.enabled && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            onNavigate?.(item.key);
          }
        }}
        className={`sidebar__nav-item ${isActive ? 'active' : ''}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          width: '100%',
          height: hubLayout ? 35 : isSub ? 40 : 48,
          flexShrink: hasAccountFooter ? 0 : undefined,
          padding: collapsed ? 0 : `${hubLayout ? '0.3rem' : '0.55rem'} ${leftPad}`,
          marginBottom,
          cursor: item.enabled ? 'pointer' : 'default',
          borderRadius: isActive ? 8 : 0,
          transition: 'background 150ms',
          opacity: item.enabled ? 1 : 0.5,
          color: hubLayout && item.enabled && !isActive ? '#6B7280' : undefined,
          fontWeight: hubLayout ? 400 : undefined,
        }}
      >
        <span style={{ fontSize: hubLayout ? 15 : isSub ? 15 : 18, width: hubLayout ? 18 : 22, textAlign: 'center', color: item.enabled ? 'inherit' : '#666' }}>
          {item.icon}
        </span>
        {!collapsed && (
          <span style={{
            marginLeft: hubLayout ? 10 : 12,
            fontSize: hubLayout ? 14 : isSub ? '0.9rem' : '1rem',
            lineHeight: hubLayout ? '20px' : undefined,
            color: item.enabled ? 'inherit' : '#666',
            opacity: collapsed ? 0 : 1,
            transition: 'opacity 200ms ease-in-out',
            whiteSpace: 'nowrap'
          }}>
            {item.label}
          </span>
        )}
      </li>
    );

    if (!item.enabled) {
      return (
        <OverlayTrigger
          key={item.key}
          placement="right"
          overlay={<Tooltip id={`tooltip-${item.key}`}>Coming soon</Tooltip>}
        >
          {navItem}
        </OverlayTrigger>
      );
    }

    return navItem;
  };

  const handleLogout = () => {
    try {
      localStorage.removeItem('managerToken');
      localStorage.removeItem('managerUser');
      localStorage.removeItem('hasDoneInitialRedirect');
    } catch (_) {}
    window.location.href = '/login';
  };

  return (
    <aside
      className={`sidebar${mobile ? ' sidebar--mobile' : ''}`}
      aria-label="Dashboard navigation"
      style={{
        width,
        position: 'fixed',
        top: 0,
        left: 0,
        height: '100vh',
        background: '#fff',
        borderRight: '1px solid #E6E8EC',
        transition: 'width 240ms',
        zIndex: 1000,
        overflow: 'hidden',
        paddingTop: hubLayout ? '2rem' : '1.5rem',
        paddingBottom: hasAccountFooter ? '0.75rem' : undefined,
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
    >
      <div
        className="sidebar__brand-wrap"
        style={{
          height: hubLayout ? 52 : 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          paddingLeft: collapsed ? 0 : '1rem',
          marginBottom: hubLayout ? '1.75rem' : '2rem',
          flexShrink: 0,
        }}
      >
        {/* Full Logo - only visible when expanded */}
        <img
          src="/sidebar.png"
          alt="Hudson"
          style={{
            width: 120,
            height: 'auto',
            opacity: collapsed ? 0 : 1,
            transition: collapsed ? 'none' : 'opacity 200ms ease-in-out',
            display: collapsed ? 'none' : 'block',
          }}
        />
        {/* Thumbnail Logo - only visible when collapsed */}
        <img
          src="/sidebar-min.png"
          alt="Hudson"
          style={{
            width: 32,
            height: 'auto',
            opacity: collapsed ? 1 : 0,
            transition: collapsed ? 'none' : 'opacity 200ms ease-in-out',
            display: collapsed ? 'block' : 'none',
          }}
        />
      </div>

      <ul
        className={`sidebar__nav${hubLayout ? ' sidebar__nav--hub' : ''}`}
        style={{
          padding: 0,
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: hubLayout ? 0 : undefined,
          flex: hasAccountFooter ? '1 1 auto' : 1,
          minHeight: hasAccountFooter ? 0 : undefined,
          overflowY: hasAccountFooter ? 'auto' : undefined,
          overflowX: hasAccountFooter ? 'hidden' : undefined,
          listStyle: 'none',
        }}
      >
        {/* Spacing is per-item (marginBottom inside renderNavItem) rather
            than a uniform `gap` because section headers + sub-items need
            tighter spacing than the default top-level rhythm. */}
        {itemsToRender.map((item, idx) => {
          if (hubLayout && item.kind !== 'header') {
            const headerKey = headerKeyForItem(itemsToRender, item.key);
            if (headerKey && collapsedSections.has(headerKey)) return null;
          }
          return renderNavItem(item, idx);
        })}
      </ul>

      {!isCorporateDashboard && <div className="sidebar__profile-wrap">
        <button type="button" aria-label="Profile" title="Profile" aria-current={activeKey === 'profile' ? 'page' : undefined} className={`sidebar__profile-item${activeKey === 'profile' ? ' active' : ''}`} onClick={()=>navigate((process.env.NODE_ENV === 'development' && process.env.REACT_APP_PROFILE_PREVIEW === 'true' ? '/profile-preview' : '/profile') + window.location.search)} style={{justifyContent:collapsed?'center':'flex-start',padding:collapsed?0:'0.55rem 0.75rem'}}><span style={{fontSize:18,width:22,textAlign:'center'}}><FaAddressCard/></span>{!collapsed&&<span>Profile</span>}</button>
      </div>}
      {showManagerAccount && (
        <SidebarAccount
          name={buildingName || 'Your building'}
          subtitle={managerName || 'Property manager'}
          initials={managerInitials}
          photoUrl={buildingPhotoUrl}
          collapsed={collapsed}
          wrapName
          onLogout={handleLogout}
        />
      )}

      {/* Account controls stay outside the scrolling navigation. */}
      {hubLayout && internalUser && (
        <InternalSidebarAccount user={internalUser} onLogout={handleLogout} />
      )}
      {showLogout && !showManagerAccount && !(hubLayout && internalUser) && (
      <div
        className="sidebar__logout-wrap"
        style={{
          marginTop: 0,
          padding: hubLayout ? '0.25rem 0.75rem' : collapsed ? '0.75rem 0' : '0.75rem',
          borderTop: '1px solid #E6E8EC',
          flexShrink: 0,
          background: '#fff',
        }}
      >
        <button
          type="button"
          onClick={handleLogout}
          className="sidebar__logout"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            gap: collapsed ? 0 : hubLayout ? 10 : 12,
            width: '100%',
            padding: hubLayout ? '0.4rem 0.75rem' : collapsed ? '0.6rem 0' : '0.6rem 0.75rem',
            background: 'transparent',
            border: 'none',
            borderRadius: 8,
            color: '#6b7280',
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: hubLayout ? 14 : '0.9rem',
            fontWeight: hubLayout ? 400 : 500,
            textAlign: 'left',
            transition: 'background 150ms, color 150ms',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#fef2f2';
            e.currentTarget.style.color = '#dc2626';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = '#6b7280';
          }}
          title="Log out"
        >
          <span style={{ fontSize: hubLayout ? 15 : 18, width: hubLayout ? 18 : 24, textAlign: 'center', display: 'inline-flex', justifyContent: 'center' }}>
            <FaSignOutAlt />
          </span>
          {!collapsed && <span style={{ whiteSpace: 'nowrap' }}>Log out</span>}
        </button>
      </div>
      )}
    </aside>
  );
}


// A modal drawer supplies focus trapping, Escape/backdrop dismissal, focus
// restoration and scroll locking. Desktop keeps its existing sidebar behavior.
export default function Sidebar(props) {
  const location = useLocation();
  const [isMobile, setIsMobile] = React.useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(max-width: 991px)').matches : false
  );
  const [open, setOpen] = React.useState(false);
  const menuId = React.useId();
  React.useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia('(max-width: 991px)');
    const update = () => { setIsMobile(media.matches); if (!media.matches) setOpen(false); };
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  React.useEffect(() => { setOpen(false); }, [location.pathname, location.search, props.activeKey]);
  React.useEffect(() => {
    if (isMobile) window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [location.pathname, isMobile]);

  if (!isMobile) return <SidebarContent {...props} />;
  const items = props.tabs || navItems;
  const title = items.find((item) => item.key === props.activeKey)?.label
    || ({ profile: 'Profile', projects: 'Projects', valveAudit: 'Valve Audit' })[props.activeKey]
    || 'Dashboard';
  return <>
    {createPortal(
      <header className="dashboard-mobile-header">
        <button type="button" className="dashboard-menu-button" aria-label="Open navigation menu"
          aria-expanded={open} aria-controls={menuId} onClick={() => setOpen(true)}>
          <FaBars aria-hidden="true" />
        </button>
        <span className="dashboard-mobile-title">{title}</span>
        <img src="/sidebar-min.png" alt="Hudson" />
      </header>, document.body
    )}
    <Offcanvas show={open} onHide={() => setOpen(false)} placement="start"
      id={menuId} className="dashboard-mobile-drawer" aria-labelledby={`${menuId}-title`}>
      <Offcanvas.Header closeButton closeLabel="Close navigation menu">
        <Offcanvas.Title id={`${menuId}-title`}>Menu</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <SidebarContent {...props} collapsed={false} mobile
          onNavigate={(key) => { setOpen(false); props.onNavigate?.(key); }} />
      </Offcanvas.Body>
    </Offcanvas>
  </>;
}

Sidebar.propTypes = {
  activeKey : PropTypes.string,
  onNavigate: PropTypes.func,
  collapsed : PropTypes.bool,
  onToggle  : PropTypes.func,
  tabs      : PropTypes.array,
  buildingName: PropTypes.string,
  buildingPhotoUrl: PropTypes.string,
  managerName: PropTypes.string,
  showProjectsTab: PropTypes.bool,
  showValveAuditTab: PropTypes.bool,
  showLogout: PropTypes.bool,
  hubLayout: PropTypes.bool,
  defaultCollapsedSections: PropTypes.arrayOf(PropTypes.string),
  internalUser: PropTypes.object,
};
